import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Typography,
  Box,
  Paper,
  Modal,
  Button,
} from "@mui/material";
import {
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
} from "@mui/icons-material";
import { API_ENDPOINT } from "../../../services/api/resources";
import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import classes from "../../TabStyles/DashboardTab.module.css";
import { Skeleton } from "@mui/material";
import DButtonOutlined from "../../../components/Dashboard/DashboardButtons/dButtonOutlined";
import Logo from "../../../assets/images/svg/Logo.svg";
import { useSelector } from "react-redux";

const GstAccordionTable = () => {
  const loginData = useSelector((state) => state.loginData.value);
  const buyer_token = loginData?.user?.token;

  const [vendors, setVendors] = useState([]);
  // const [vendors, setVendors] = useState(vendor);
  const [openRows, setOpenRows] = useState({});

  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const [selectedSummary, setSelectedSummary] = useState("");

  // useEffect(() => {
  //     setLoading(true)
  //     axios.post(API_ENDPOINT.gst_filing_details).then(res => {
  //         setVendors(res.data || []);
  //         setLoading(false)
  //     });
  // }, []);

  const fetchVendorGSTFiling = async () => {
    try {
      const response = await axios.post(API_ENDPOINT.gst_filing_details, {
        buyer_token: buyer_token,
      });
      setVendors(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching logs:", error);
    }
  };

  useEffect(() => {
    fetchVendorGSTFiling();
  }, []);

  const toggleRow = (index) => {
    setOpenRows((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const toggleAllRows = () => {
    const anyOpen = Object.values(openRows).some((isOpen) => isOpen);
    const updated = {};
    vendors.forEach((_, idx) => {
      updated[idx] = !anyOpen;
    });
    setOpenRows(updated);
  };

  const extractReturns = (filing_status, type) => {
    return (filing_status?.result || []).flatMap(
      (period) =>
        (period.eFiledlist || [])
          .filter((r) => r.rtntype === type)
          .map((r) => {
            const hasValidDueDate =
              r.dueDt && /^\d{4}-\d{2}-\d{2}$/.test(r.dueDt);
            const hasValidDOF = r.dof && /^\d{2}-\d{2}-\d{4}$/.test(r.dof);

            let earlyDays = null;

            if (hasValidDueDate && hasValidDOF && r.isDelay === false) {
              const dateOfFiling = new Date(
                r.dof.split("-").reverse().join("-")
              );
              const dueDate = new Date(r.dueDt);

              if (!isNaN(dateOfFiling) && !isNaN(dueDate)) {
                const diffTime = dueDate - dateOfFiling;
                earlyDays =
                  diffTime > 0
                    ? Math.floor(diffTime / (1000 * 60 * 60 * 24))
                    : 0;
              }
            }

            return {
              finYear: period.financialYear,
              taxPeriod: formatTaxPeriod(r.retPrd),
              dateOfFiling: r.dof,
              status: r.status,
              delayDays: r.delayDays,
              isDelay: r.isDelay,
              earlyFilingDays: earlyDays ?? "N/A",
            };
          })

      // .map(r => ({
      //     // finYear: period.financialYear,
      //     // taxPeriod: formatTaxPeriod(r.retPrd),
      //     // dateOfFiling: r.dof,
      //     // status: r.status,
      //     // mof: r.mof,
      //     // delayDays: r.delayDays,
      //     // isDelay: r.isDelay
      // }))
    );
  };

  const formatTaxPeriod = (retPrd) => {
    const monthMap = {
      "01": "January",
      "02": "February",
      "03": "March",
      "04": "April",
      "05": "May",
      "06": "June",
      "07": "July",
      "08": "August",
      "09": "September",
      10: "October",
      11: "November",
      12: "December",
    };
    const month = retPrd?.slice(0, 2);
    return monthMap[month] || retPrd;
  };

  const getSummaryText = (vendor) => {
    const gstr3b = extractReturns(vendor.filing_status, "GSTR3B");
    if (!gstr3b.length) return "Summary: Nil";

    const finYears = new Set();
    const delayDetails = [];

    gstr3b.forEach((f) => {
      finYears.add(f.finYear);
      if (f.isDelay)
        delayDetails.push({ month: f.taxPeriod, days: f.delayDays || 0 });
    });

    const delayMonths = delayDetails.length;
    const delayCount = delayDetails.reduce(
      (acc, d) => acc + (d.days > 0 ? 1 : 0),
      0
    );
    const score = (() => {
      const successRatio = ((gstr3b.length - delayCount) / gstr3b.length) * 100;
      if (successRatio >= 90) return "Excellent";
      if (successRatio >= 70) return "Good";
      return "Average";
    })();

    const monthsLine = delayDetails
      .map((d, i) => `the month of ${d.month} is ${d.days} day(s)`)
      .join(", and ");

    return `This entity, <strong>${
      vendor.company_name
    } </strong>, has filed GST for the financial year(s) ${Array.from(
      finYears
    ).join(
      ", "
    )} with ${delayCount} delay(s) over a period of ${delayMonths} month(s). ${monthsLine}. A total of ${delayCount} delay(s) were found in this report. Therefore, their score is ${score}.

        Despite the delays, it is important to note the overall compliance behavior, including early and on-time submissions, which also contribute to the entity's compliance performance. Based on the filing pattern and weighted scoring methodology that accounts for timely and proactive filings, Teckost has achieved a compliance score of ${score}
        `;
  };

  return (
    <>
      <HeaderTitle titleText={"Vendor GST Filing Analysis and Compliance"} />
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ whiteSpace: "noWrap" }}>
              <TableCell onClick={toggleAllRows} sx={{ cursor: "pointer" }}>
                <ArrowUpIcon />
                <ArrowDownIcon />
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "14px" }}>
                Company Name
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "14px" }}>
                GSTIN
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "14px" }}>
                PAN
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "14px" }}>
                Defaulter
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "14px" }}>
                Delay in Filing
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "14px" }}>
                Delay %
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "14px" }}>
                Early Filing %
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "14px" }}>
                Score
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "14px" }}>
                Summary
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading
              ? Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 10 }).map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : vendors.map((vendor, index) => {
                  const gstr3b = extractReturns(vendor.filing_status, "GSTR3B");
                  const isOpen = openRows[index];

                  const totalFiled = gstr3b.length;
                  const delayCount = gstr3b.filter((r) => r.isDelay).length;
                  const earlyCount = gstr3b.filter(
                    (r) => r.earlyFilingDays > 0
                  ).length;

                  const delayedFiled = gstr3b.filter(
                    (row) => row.isDelay === true
                  ).length;
                  const successFiled = totalFiled - delayedFiled;

                  const delayPercent = totalFiled
                    ? Math.round((delayCount / totalFiled) * 100)
                    : 0;
                  const earlyPercent =
                    totalFiled > 0
                      ? Math.round((successFiled / totalFiled) * 100)
                      : 0;

                  return (
                    <React.Fragment key={index}>
                      <TableRow>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => toggleRow(index)}
                          >
                            {isOpen ? <ArrowUpIcon /> : <ArrowDownIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>
                          {vendor.company_name}
                        </TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>
                          {vendor.filing_status?.gstin || "N/A"}
                        </TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>
                          {vendor.pan_number}
                        </TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>
                          {vendor.filing_status?.complianceStatus
                            ?.isDefaulter === true
                            ? "Yes"
                            : vendor.filing_status?.complianceStatus
                                ?.isDefaulter === false
                            ? "No"
                            : "N/A"}
                        </TableCell>

                        <TableCell sx={{ fontSize: "12px" }}>
                          {vendor.filing_status?.complianceStatus
                            ?.isAnyDelay === true
                            ? "Yes"
                            : vendor.filing_status?.complianceStatus
                                ?.isAnyDelay === false
                            ? "No"
                            : "N/A"}
                        </TableCell>

                        <TableCell sx={{ fontSize: "12px" }}>
                          {/* {(() => {
                                            const gstr3b = extractReturns(vendor.filing_status, 'GSTR3B');
                                            const total = gstr3b.length;
                                            console.log(total)
                                            const delayed = gstr3b.filter(r => r.isDelay === true).length;
                                            return total === 0 ? '0%' : `${((delayed / total) * 100).toFixed(1)}%`;
                                        })()} */}
                          {delayPercent}%
                        </TableCell>

                        <TableCell sx={{ fontSize: "12px" }}>
                          {earlyPercent}%
                        </TableCell>

                        {/* <TableCell>
                                        {(() => {
                                            const gstr3b = extractReturns(vendor.filing_status, 'GSTR3B');
                                            const total = gstr3b.length;
                                            const timely = gstr3b.filter(r => r.isDelay === false).length;
                                            return total === 0 ? '0%' : `${((timely / total) * 100).toFixed(1)}%`;
                                        })()}
                                    </TableCell> */}

                        <TableCell sx={{ fontSize: "12px" }}>
                          {(() => {
                            const gstr3b = extractReturns(
                              vendor.filing_status,
                              "GSTR3B"
                            );
                            if (!gstr3b || gstr3b.length === 0) return "-";
                            const delayed = gstr3b.filter(
                              (r) => r.isDelay === true
                            );
                            const percentage =
                              gstr3b.length === 0
                                ? 0
                                : (delayed.length / gstr3b.length) * 100;
                            if (percentage <= 10) return "Excellent";
                            if (percentage <= 30) return "Good";
                            if (percentage <= 60) return "Average";
                            return "Poor";
                          })()}
                        </TableCell>

                        <TableCell>
                          <Button
                            variant="contained"
                            onClick={() => {
                              setSelectedSummary(getSummaryText(vendor));
                              setModalOpen(true);
                            }}
                          >
                            View Summary
                          </Button>
                        </TableCell>
                      </TableRow>

                      {isOpen && (
                        <TableRow>
                          <TableCell
                            colSpan={10}
                            sx={{ p: 0, borderBottom: 0 }}
                          >
                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2 }}>
                                <Table size="large">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell
                                        sx={{
                                          fontWeight: "bold",
                                          fontSize: "12px",
                                        }}
                                      >
                                        Financial Year
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontWeight: "bold",
                                          fontSize: "12px",
                                        }}
                                      >
                                        Month
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontWeight: "bold",
                                          fontSize: "12px",
                                        }}
                                      >
                                        Date of Filing
                                      </TableCell>
                                      {/* <TableCell>Mode of Filling</TableCell> */}
                                      <TableCell
                                        sx={{
                                          fontWeight: "bold",
                                          fontSize: "12px",
                                        }}
                                      >
                                        Delayed Filing
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontWeight: "bold",
                                          fontSize: "12px",
                                        }}
                                      >
                                        Delay in Days
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontWeight: "bold",
                                          fontSize: "12px",
                                        }}
                                      >
                                        Early Filing Days
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontWeight: "bold",
                                          fontSize: "12px",
                                        }}
                                      >
                                        Status
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {gstr3b.length > 0 ? (
                                      gstr3b.map((row, i) => (
                                        <TableRow key={i}>
                                          <TableCell sx={{ fontSize: "12px" }}>
                                            {row.finYear}
                                          </TableCell>
                                          <TableCell sx={{ fontSize: "12px" }}>
                                            {row.taxPeriod}
                                          </TableCell>
                                          <TableCell sx={{ fontSize: "12px" }}>
                                            {row.dateOfFiling}
                                          </TableCell>
                                          {/* <TableCell>{row.mof}</TableCell> */}
                                          <TableCell sx={{ fontSize: "12px" }}>
                                            {row.isDelay ? "Yes" : "No"}
                                          </TableCell>
                                          <TableCell sx={{ fontSize: "12px" }}>
                                            {row.delayDays}
                                          </TableCell>
                                          <TableCell sx={{ fontSize: "12px" }}>
                                            {row.earlyFilingDays ?? "N/A"}
                                          </TableCell>
                                          <TableCell sx={{ fontSize: "12px" }}>
                                            {row.status}
                                          </TableCell>
                                        </TableRow>
                                      ))
                                    ) : (
                                      <TableRow>
                                        <TableCell colSpan={7} align="center">
                                          No GSTR3B filings found
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
          </TableBody>
        </Table>

        <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 600,
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px",
                background: "#e9e9e9",
                borderRadius: "5px",
                marginBottom: "10px",
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontSize: "14px", fontWeight: "600" }}
              >
                Summary of the Report
              </Typography>
              <img src={Logo} width={"80"} />
            </div>
            {/* <Typography variant="body1" sx={{ fontSize: '12px',marginTop:'12' }}>{selectedSummary}</Typography> */}
            <Typography
              variant="body2"
              dangerouslySetInnerHTML={{ __html: selectedSummary }}
              style={{ whiteSpace: "pre-line" }}
              sx={{ fontSize: "12px" }}
            />

            <Box mt={2} textAlign="right">
              <Button onClick={() => setModalOpen(false)} variant="contained">
                Close
              </Button>
            </Box>
          </Box>
        </Modal>
      </TableContainer>
    </>
  );
};

export default GstAccordionTable;
